<script>
	import { onMount } from 'svelte';
	import Loader from './Loader.svelte';
	import TabView from './TabView.svelte';
	import TabPane from './TabPane.svelte';
	import WidgetSelect from './WidgetSelect.svelte';
	import { buildQuery, gameUrl, getApiUrl, getStatValue, injectGlobalStyles, loadJson, normalizeText, teamUrl } from './shared.js';

	export let personId = '';
	export let teamId = '';
	export let tag = 'mlbl';
	export let season = '';
	export let lang = 'ru';

	let host;
	let loading = false;
	let careerLoading = false;
	let statsLoading = false;
	let errorMessage = '';
	let player = null;
	let careerItems = [];
	let stats = [];
	let selectedSeason = '';
	let statsErrorMessage = '';

	$: normalCareerRows = careerItems.filter((item) => item?.class === 'Normal');
	$: averageCareerRow = careerItems.find((item) => item?.class === 'Avg') || null;
	$: summaryCareerRow = careerItems.find((item) => item?.class === 'Sum') || null;

	$: seasonOptions = Array.from(new Set(normalCareerRows.map((item) => item?.season?.id || item?.season?.name)))
		.filter(Boolean)
		.map((seasonId, index) => {
			const season = normalCareerRows.find((item) => (item?.season?.id || item?.season?.name) === seasonId);
			return {
				value: String(seasonId),
				label: normalizeText(season?.season?.name || season?.season?.fullName || seasonId, `Сезон ${index + 1}`)
			};
		});

	$: normalStatsRows = stats.filter((item) => item?.class === 'Normal');
	$: sumStatsRow = stats.find((item) => item?.class === 'Sum') || null;
	$: avgStatsRow = stats.find((item) => item?.class === 'Avg') || null;

	function getPlayerTitle(data) {
		return data?.fullName || [data?.firstName, data?.lastName].filter(Boolean).join(' ') || 'Игрок';
	}

	function getPositionTitle(data) {
		return data?.position?.name || data?.position || '—';
	}

	function getInitials(data) {
		const first = data?.firstName?.[0] || '';
		const last = data?.lastName?.[0] || '';
		return `${first}${last}`.trim() || 'P';
	}

	function formatPercent(value) {
		const text = normalizeText(value);
		if (text === '—') {
			return text;
		}
		return String(text).includes('%') ? String(text) : `${text}%`;
	}

	function formatStartsCheck(value) {
		if (value === 'true' || value === true || value === 1 || value === '1') {
			return '✓';
		}
		return '';
	}

	function getTeamId(team) {
		return team?.id || team?.teamId || '';
	}

	async function loadPlayer() {
		if (!personId) {
			errorMessage = 'Не указан идентификатор игрока';
			player = null;
			careerItems = [];
			return;
		}

		loading = true;
		careerLoading = true;
		errorMessage = '';
		try {
			const query = buildQuery({ teamId, tag, season, lang });
			const [playerInfo, career] = await Promise.all([
				loadJson(`${getApiUrl()}/ib/player/${encodeURIComponent(personId)}/info${query}`),
				loadJson(`${getApiUrl()}/ib/player/${encodeURIComponent(personId)}/career${query}`)
			]);

			player = playerInfo;
			careerItems = Array.isArray(career) ? career : [];
			selectedSeason = computeFirstSeason(careerItems);
		} catch (error) {
			console.error('Load player info error:', error);
			errorMessage = 'Ошибка загрузки игрока';
			player = null;
			careerItems = [];
		} finally {
			loading = false;
			careerLoading = false;
		}
	}

	function computeFirstSeason(items) {
		for (const item of items || []) {
			if (item?.class !== 'Normal') continue;
			const seasonId = item?.season?.id || item?.season?.name;
			if (seasonId) return String(seasonId);
		}
		return '';
	}

	async function loadStats() {
		if (!personId || !selectedSeason) {
			stats = [];
			return;
		}

		statsLoading = true;
		statsErrorMessage = '';
		try {
			const query = buildQuery({ tag, season: selectedSeason, lang });
			stats = await loadJson(`${getApiUrl()}/ib/player/${encodeURIComponent(personId)}/stats${query}`);
			if (!Array.isArray(stats)) {
				stats = [];
			}
		} catch (error) {
			console.error('Load player stats error:', error);
			statsErrorMessage = 'Ошибка загрузки статистики';
			stats = [];
		} finally {
			statsLoading = false;
		}
	}

	$: if (seasonOptions.length > 0 && !seasonOptions.some((option) => option.value === selectedSeason)) {
		selectedSeason = seasonOptions[0].value;
	}

	$: if (selectedSeason && player) {
		loadStats();
	}

	onMount(() => {
		const root = host?.getRootNode();
		if (root instanceof ShadowRoot) {
			injectGlobalStyles(root);
		}

		loadPlayer();
	});
</script>

<div class="widget-card" bind:this={host}>
	{#if loading}
		<div class="widget-card__status"><Loader size="64px" /></div>
	{:else if errorMessage}
		<div class="widget-card__status">{errorMessage}</div>
	{:else if player}
		<div class="profile">
			<div class="profile__head">
				<div class="profile__media">
					{#if player.photo}
						<img class="profile__image" src={player.photo} alt={getPlayerTitle(player)}>
					{:else}
						<div class="profile__placeholder">{getInitials(player)}</div>
					{/if}
				</div>

				<div class="profile__title">
					<h3>{getPlayerTitle(player)}</h3>
					<p>{getPositionTitle(player)}</p>
				</div>
			</div>

			<div class="profile__metrics">
				<div class="profile__metric">
					<span>Рост</span>
					<strong>{player.height ? `${player.height} см` : '—'}</strong>
				</div>
				<div class="profile__metric">
					<span>Вес</span>
					<strong>{player.weight ? `${player.weight} кг` : '—'}</strong>
				</div>
				<div class="profile__metric">
					<span>Возраст</span>
					<strong>{normalizeText(player.age)}</strong>
				</div>
			</div>

			<TabView>

				<TabPane title="Карьера">
					<div class="widget-tabs__body">
						{#if careerLoading}
							<div class="widget-card__status"><Loader size="64px" /></div>
						{:else if !careerItems.length}
							<div class="widget-card__status">Данные о карьере не найдены</div>
						{:else}
							<div class="stats_table-wrap">
								<table class="stats_table career-table">
									<thead>
										<tr>
											<th rowspan="2">Сезон</th>
											<th rowspan="2">Команда</th>
											<th rowspan="2">Игры</th>
											<th rowspan="2">5</th>
											<th rowspan="2">СВ</th>
											<th rowspan="2">Очки</th>
											<th colspan="3">Броски</th>
											<th colspan="3">Подборы</th>
											<th rowspan="2">ГП</th>
											<th rowspan="2">ПХ</th>
											<th rowspan="2">ПТ</th>
											<th rowspan="2">БШ</th>
											<th rowspan="2">Ф</th>
											<th rowspan="2">ФС</th>
											<th rowspan="2">+/-</th>
										</tr>
										<tr>
											<th>2-очк</th>
											<th>3-очк</th>
											<th>ШБ</th>
											<th>СЩ</th>
											<th>ЧЩ</th>
											<th>ВС</th>
										</tr>
									</thead>
									<tbody>
										{#each normalCareerRows as item}
											<tr>
												<td>{normalizeText(item?.season?.name)}</td>
												<td>
													{#if getTeamId(item?.team)}
														<a href={teamUrl(getTeamId(item.team), { tag, season: item?.season?.id || item?.season?.name })} class="career-table__team-link">
															{normalizeText(item?.team?.shortName || item?.team?.name || item?.team?.label)}
														</a>
													{:else}
														{normalizeText(item?.team?.shortName || item?.team?.name || item?.team?.label)}
													{/if}
												</td>
												<td>{normalizeText(item?.stats?.games)}</td>
												<td>{getStatValue(item?.stats, 'isStarts')}</td>
												<td>{getStatValue(item?.stats, 'playedTime')}</td>
												<td>{getStatValue(item?.stats, 'points')}</td>
												<td class="career-table__percent">{formatPercent(getStatValue(item?.stats, 'shot2Percent'))}</td>
												<td class="career-table__percent">{formatPercent(getStatValue(item?.stats, 'shot3Percent'))}</td>
												<td class="career-table__percent">{formatPercent(getStatValue(item?.stats, 'shot1Percent'))}</td>
												<td>{getStatValue(item?.stats, 'defRebound')}</td>
												<td>{getStatValue(item?.stats, 'offRebound')}</td>
												<td>{getStatValue(item?.stats, 'rebound')}</td>
												<td>{getStatValue(item?.stats, 'assist')}</td>
												<td>{getStatValue(item?.stats, 'steal')}</td>
												<td>{getStatValue(item?.stats, 'turnover')}</td>
												<td>{getStatValue(item?.stats, 'blockShot')}</td>
												<td>{getStatValue(item?.stats, 'foul')}</td>
												<td>{getStatValue(item?.stats, 'foulsOnPlayer')}</td>
												<td>{getStatValue(item?.stats, 'plusMinus')}</td>
											</tr>
										{/each}
										{#if summaryCareerRow}
										<tr class="career-table__summary-row">
											<td colspan="2">Всего за карьеру</td>
											<td>{normalizeText(summaryCareerRow?.stats?.games)}</td>
											<td>{getStatValue(summaryCareerRow?.stats, 'isStarts')}</td>
											<td>{getStatValue(summaryCareerRow?.stats, 'playedTime')}</td>
											<td>{getStatValue(summaryCareerRow?.stats, 'points')}</td>
											<td class="career-table__percent">{formatPercent(getStatValue(summaryCareerRow?.stats, 'shot2Percent'))}</td>
											<td class="career-table__percent">{formatPercent(getStatValue(summaryCareerRow?.stats, 'shot3Percent'))}</td>
											<td class="career-table__percent">{formatPercent(getStatValue(summaryCareerRow?.stats, 'shot1Percent'))}</td>
											<td>{getStatValue(summaryCareerRow?.stats, 'defRebound')}</td>
											<td>{getStatValue(summaryCareerRow?.stats, 'offRebound')}</td>
											<td>{getStatValue(summaryCareerRow?.stats, 'rebound')}</td>
											<td>{getStatValue(summaryCareerRow?.stats, 'assist')}</td>
											<td>{getStatValue(summaryCareerRow?.stats, 'steal')}</td>
											<td>{getStatValue(summaryCareerRow?.stats, 'turnover')}</td>
											<td>{getStatValue(summaryCareerRow?.stats, 'blockShot')}</td>
											<td>{getStatValue(summaryCareerRow?.stats, 'foul')}</td>
											<td>{getStatValue(summaryCareerRow?.stats, 'foulsOnPlayer')}</td>
											<td>{getStatValue(summaryCareerRow?.stats, 'plusMinus')}</td>
										</tr>
										{/if}
										{#if averageCareerRow}
										<tr class="career-table__summary-row">
												<td colspan="3">В среднем</td>
												<td>{getStatValue(averageCareerRow?.stats, 'isStarts')}</td>
												<td>{getStatValue(averageCareerRow?.stats, 'playedTime')}</td>
												<td>{getStatValue(averageCareerRow?.stats, 'points')}</td>
												<td class="career-table__percent">{formatPercent(getStatValue(averageCareerRow?.stats, 'shot2Percent'))}</td>
												<td class="career-table__percent">{formatPercent(getStatValue(averageCareerRow?.stats, 'shot3Percent'))}</td>
												<td class="career-table__percent">{formatPercent(getStatValue(averageCareerRow?.stats, 'shot1Percent'))}</td>
												<td>{getStatValue(averageCareerRow?.stats, 'defRebound')}</td>
												<td>{getStatValue(averageCareerRow?.stats, 'offRebound')}</td>
												<td>{getStatValue(averageCareerRow?.stats, 'rebound')}</td>
												<td>{getStatValue(averageCareerRow?.stats, 'assist')}</td>
												<td>{getStatValue(averageCareerRow?.stats, 'steal')}</td>
												<td>{getStatValue(averageCareerRow?.stats, 'turnover')}</td>
												<td>{getStatValue(averageCareerRow?.stats, 'blockShot')}</td>
												<td>{getStatValue(averageCareerRow?.stats, 'foul')}</td>
												<td>{getStatValue(averageCareerRow?.stats, 'foulsOnPlayer')}</td>
												<td>{getStatValue(averageCareerRow?.stats, 'plusMinus')}</td>
											</tr>
										{/if}
									</tbody>
								</table>
							</div>
						{/if}
					</div>
				</TabPane>

				<TabPane title="Статистика">
					<div class="widget-tabs__body">
						<div class="stats-toolbar">
							<WidgetSelect
								className="--black"
								name="player-stats-season"
								options={seasonOptions}
								bind:value={selectedSeason}
								disabled={statsLoading || !seasonOptions.length}
							/>
						</div>

						{#if statsLoading}
							<div class="widget-card__status"><Loader size="64px" /></div>
						{:else if statsErrorMessage}
							<div class="widget-card__status">{statsErrorMessage}</div>
						{:else if !normalStatsRows.length}
							<div class="widget-card__status">Статистика не найдена</div>
						{:else}
							<div class="stats_table-wrap">
								<table class="stats_table player-stats-table">
									<thead>
										<tr>
											<th rowspan="2">Дата</th>
											<th rowspan="2">Команда А</th>
											<th rowspan="2">Команда Б</th>
											<th rowspan="2">Счет</th>
											<th rowspan="2">5</th>
											<th rowspan="2">СВ</th>
											<th rowspan="2">Очки</th>
											<th colspan="3">Броски</th>
											<th colspan="3">Подборы</th>
											<th rowspan="2">ГП</th>
											<th rowspan="2">ПХ</th>
											<th rowspan="2">ПТ</th>
											<th rowspan="2">БШ</th>
											<th rowspan="2">Ф</th>
											<th rowspan="2">ФС</th>
											<th rowspan="2">+/-</th>
										</tr>
										<tr>
											<th>2-очк</th>
											<th>3-очк</th>
											<th>ШБ</th>
											<th>СЩ</th>
											<th>ЧЩ</th>
											<th>ВС</th>
										</tr>
									</thead>
									<tbody>
										{#each normalStatsRows as item}
											<tr>
												<td>{normalizeText(item?.game?.gameDate)}</td>
												<td>
													{#if item?.game?.team1Id}
														<a href={teamUrl(item.game.team1Id, { tag })} class="career-table__team-link">
															{normalizeText(item?.game?.team1Name || item?.team?.name || item?.team?.label)}
														</a>
													{:else}
														{normalizeText(item?.game?.team1Name || item?.team?.name || item?.team?.label)}
													{/if}
												</td>
												<td>
													{#if item?.game?.team2Id}
														<a href={teamUrl(item.game.team2Id, { tag })} class="career-table__team-link">
															{normalizeText(item?.game?.team2Name)}
														</a>
													{:else}
														{normalizeText(item?.game?.team2Name)}
													{/if}
												</td>
												<td>
													{#if item?.game?.id}
														<a href={gameUrl(item.game.id, { tag, teamId })} style="cursor: pointer; color: #0066cc;">
															{normalizeText(item?.game?.score)}
														</a>
													{:else}
														{normalizeText(item?.game?.score)}
													{/if}
												</td>
												<td>{formatStartsCheck(item?.stats?.isStarts)}</td>
												<td>{getStatValue(item?.stats, 'playedTime')}</td>
												<td>{getStatValue(item?.stats, 'points')}</td>
												<td class="career-table__percent">{formatPercent(getStatValue(item?.stats, 'shot2Percent'))}</td>
												<td class="career-table__percent">{formatPercent(getStatValue(item?.stats, 'shot3Percent'))}</td>
												<td class="career-table__percent">{formatPercent(getStatValue(item?.stats, 'shot1Percent'))}</td>
												<td>{getStatValue(item?.stats, 'defRebound')}</td>
												<td>{getStatValue(item?.stats, 'offRebound')}</td>
												<td>{getStatValue(item?.stats, 'rebound')}</td>
												<td>{getStatValue(item?.stats, 'assist')}</td>
												<td>{getStatValue(item?.stats, 'steal')}</td>
												<td>{getStatValue(item?.stats, 'turnover')}</td>
												<td>{getStatValue(item?.stats, 'blockShot')}</td>
												<td>{getStatValue(item?.stats, 'foul')}</td>
												<td>{getStatValue(item?.stats, 'foulsOnPlayer')}</td>
												<td>{getStatValue(item?.stats, 'plusMinus')}</td>
											</tr>
										{/each}
										{#if sumStatsRow}
											<tr class="career-table__summary-row">
												<td colspan="4">ИТОГО:</td>
												<td>{formatStartsCheck(sumStatsRow?.stats?.isStarts)}</td>
												<td>{getStatValue(sumStatsRow?.stats, 'playedTime')}</td>
												<td>{getStatValue(sumStatsRow?.stats, 'points')}</td>
												<td class="career-table__percent">{formatPercent(getStatValue(sumStatsRow?.stats, 'shot2Percent'))}</td>
												<td class="career-table__percent">{formatPercent(getStatValue(sumStatsRow?.stats, 'shot3Percent'))}</td>
												<td class="career-table__percent">{formatPercent(getStatValue(sumStatsRow?.stats, 'shot1Percent'))}</td>
												<td>{getStatValue(sumStatsRow?.stats, 'defRebound')}</td>
												<td>{getStatValue(sumStatsRow?.stats, 'offRebound')}</td>
												<td>{getStatValue(sumStatsRow?.stats, 'rebound')}</td>
												<td>{getStatValue(sumStatsRow?.stats, 'assist')}</td>
												<td>{getStatValue(sumStatsRow?.stats, 'steal')}</td>
												<td>{getStatValue(sumStatsRow?.stats, 'turnover')}</td>
												<td>{getStatValue(sumStatsRow?.stats, 'blockShot')}</td>
												<td>{getStatValue(sumStatsRow?.stats, 'foul')}</td>
												<td>{getStatValue(sumStatsRow?.stats, 'foulsOnPlayer')}</td>
												<td>{getStatValue(sumStatsRow?.stats, 'plusMinus')}</td>
											</tr>
										{/if}
										{#if avgStatsRow}
											<tr class="career-table__summary-row">
												<td colspan="4">В среднем:</td>
												<td>{formatStartsCheck(avgStatsRow?.stats?.isStarts)}</td>
												<td>{getStatValue(avgStatsRow?.stats, 'playedTime')}</td>
												<td>{getStatValue(avgStatsRow?.stats, 'points')}</td>
												<td class="career-table__percent">{formatPercent(getStatValue(avgStatsRow?.stats, 'shot2Percent'))}</td>
												<td class="career-table__percent">{formatPercent(getStatValue(avgStatsRow?.stats, 'shot3Percent'))}</td>
												<td class="career-table__percent">{formatPercent(getStatValue(avgStatsRow?.stats, 'shot1Percent'))}</td>
												<td>{getStatValue(avgStatsRow?.stats, 'defRebound')}</td>
												<td>{getStatValue(avgStatsRow?.stats, 'offRebound')}</td>
												<td>{getStatValue(avgStatsRow?.stats, 'rebound')}</td>
												<td>{getStatValue(avgStatsRow?.stats, 'assist')}</td>
												<td>{getStatValue(avgStatsRow?.stats, 'steal')}</td>
												<td>{getStatValue(avgStatsRow?.stats, 'turnover')}</td>
												<td>{getStatValue(avgStatsRow?.stats, 'blockShot')}</td>
												<td>{getStatValue(avgStatsRow?.stats, 'foul')}</td>
												<td>{getStatValue(avgStatsRow?.stats, 'foulsOnPlayer')}</td>
												<td>{getStatValue(avgStatsRow?.stats, 'plusMinus')}</td>
											</tr>
										{/if}
									</tbody>
								</table>
							</div>
						{/if}
					</div>
				</TabPane>
			</TabView>
		</div>
	{/if}
</div>

<style>
	:host { display: block; }
	.widget-card {
		border: 1px solid #d7dce2;
		border-radius: 16px;
		padding: 18px;
		background: #fff;
		box-shadow: 0 6px 24px rgba(8, 22, 47, 0.05);
	}
	.widget-card__status {
		display: flex;
		justify-content: center;
		align-items: center;
		min-height: 160px;
		color: #53657b;
		text-align: center;
	}
	.profile__head {
		display: flex;
		gap: 16px;
		align-items: center;
	}
	.profile__media { flex: 0 0 auto; }
	.profile__image,
	.profile__placeholder {
		width: 88px;
		height: 88px;
		border-radius: 18px;
		object-fit: cover;
		background: #edf2f7;
	}
	.profile__placeholder {
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 28px;
		font-weight: 700;
		color: #00244d;
	}
	.profile__title h3 {
		margin: 0;
		font-size: 22px;
		color: #122033;
	}
	.profile__title p {
		margin: 6px 0 0;
		color: #53657b;
	}
	.profile__metrics {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 12px;
		margin: 18px 0 8px;
	}
	.profile__metric {
		padding: 12px 14px;
		border-radius: 12px;
		background: #f7f9fc;
	}
	.profile__metric span {
		display: block;
		margin-bottom: 6px;
		font-size: 12px;
		text-transform: uppercase;
		color: #6b7a90;
	}
	.profile__metric strong {
		font-size: 16px;
		color: #122033;
	}
	.widget-tabs__body {
		padding: 4px 0 0;
	}
	.widget-stats {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 12px;
		margin: 0;
	}
	.widget-stats div {
		padding: 12px 14px;
		border-radius: 12px;
		background: #f7f9fc;
	}
	.widget-stats dt {
		margin-bottom: 6px;
		font-size: 12px;
		text-transform: uppercase;
		color: #6b7a90;
	}
	.widget-stats dd {
		margin: 0;
		color: #122033;
		font-weight: 600;
	}
	.career-table {
		min-width: 1120px;
	}
	.career-table__percent {
		text-align: center;
	}
	.career-table__team-link {
		color: #0066cc;
		text-decoration: none;
	}
	.career-table__team-link:hover {
		text-decoration: underline;
	}
	.career-table__summary-row td {
		font-weight: 700;
		background: #f7f9fc;
	}
	.stats-toolbar {
		display: flex;
		justify-content: flex-start;
		margin-bottom: 16px;
	}
	.stats-toolbar :global(.select-wrap) {
		width: 100%;
		max-width: 250px;
	}
	.player-stats-table {
		min-width: 1400px;
	}
	@media (max-width: 640px) {
		.profile__head { align-items: flex-start; }
		.profile__metrics,
		.widget-stats { grid-template-columns: 1fr; }
	}
</style>
